import React from "react";
import { InputGroup, FormControl, FormGroup, Button } from "react-bootstrap";
import { isJa, _t } from "app/i18n/i18n";
import useIsMobile from "lib/hooks/useIsMobile";
import { isIntranetPath } from "lib/routing";
import { useSelector } from "react-redux";
import { setParam, push } from "api/search/filterUrlMaker";
import { SearchTooltip } from "components/SearchBar/SearchTooltip";
import { getGRP_JAPI_Url } from "utils/htmlReportsUtils";
import { FaIcon } from "lib/FaIcon";
import { isInternalUser } from "lib/entitlements";
import { DOMICILE } from "../../../../redux/slices/api/initialisePreferences";
import nomuClipLogoSvg from "../images/nomuClipLogo.svg";

export function SearchBox({
  keyword,
  setKeyword,
  setAcVisible = () => {}, // update to empty function
  isSearching,
  setIsSearching = () => {}, // update to empty function
  isVisible = true,
  isSearchButtonVisible = true,
  placeholder = _t("Search"),
  preventFormSubmit = false,
  autoFocus,
  className = "py-2 m-0",
  size = "sm",
  forHtmlReport = false,
  onNairaEnter,
  suppressBlurRef
}) {
  const isMobile = useIsMobile();
  const domicile = useSelector((state) => state.preferences[DOMICILE])[0];

  const shldShowNomuClip = () =>
    isInternalUser() && domicile === "ja" && isSearchButtonVisible && isIntranetPath() && !isMobile;

  return (
    <FormGroup className={`searchbar d-flex pb-2 m-0 mt-2 mt-md-0 ${isVisible ? "" : "d-none"}`}>
      <InputGroup size={size}>
        {isSearching && keyword && (
          <div className={`${isSearching && keyword && "spinner-border"} text-primary spinner-grow-sm mr-3 mt-2`}/>
        )}
        <FormControl
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setAcVisible(true);
          }}
          onBlur={() => {
            if (suppressBlurRef && suppressBlurRef.current) {
              suppressBlurRef.current = false;
              return;
            }
            setIsSearching(false);
            setAcVisible(false);
          }}
          onClick={() => keyword && setAcVisible(true)}
          onKeyDown={(e) =>
            onKeyDown(e, preventFormSubmit, setIsSearching, setAcVisible, setKeyword, forHtmlReport, onNairaEnter)  // ← ADD onNairaEnter
          }
          placeholder={placeholder}
          size={size}
          autoFocus={!!autoFocus}
          className={className}
          type="search"
        />
        {isSearchButtonVisible && (
          <SearchButton
            keyword={keyword}
            setKeyword={setKeyword}
            forHtmlReport={forHtmlReport}
            onNairaEnter={onNairaEnter}
          />
        )}
      </InputGroup>

      {isSearchButtonVisible && !forHtmlReport && <SearchTooltip className="py-0 pl-2 pr-0"/>}
      {shldShowNomuClip() && <NomuClipLogo/>}
    </FormGroup>
  );
}

const onKeyDown = (e, preventFormSubmit, setIsSearching, setAcVisible, setKeyword, forHtmlReport, onNairaEnter) => {  // ← ADD onNairaEnter
  if (e.key === "Enter") {
    if (preventFormSubmit) {
      e.preventDefault();
    }
    if (onNairaEnter) {
      onNairaEnter(e.target.value);
      setKeyword("");
      return;
    }
    setIsSearching(false);
    setAcVisible(false);
    onEnterKey(e.target.value, forHtmlReport); // update query string
    // Once enter is pressed make search-bar blank
    setKeyword("");
  }
};

export const onEnterKey = (searchText = "", forHtmlReport) => {
  searchText = searchText.trim();
  if (!searchText) return null;
  if (isJa() && isNotExact(searchText)) searchText = toExact(searchText);
  const qs = setParam("q", searchText);
  delete qs.From;
  forHtmlReport ? window.open(`${getGRP_JAPI_Url()}/research/m/Home?q=${searchText}`) : push(qs);
};

function SearchButton({keyword, setKeyword, forHtmlReport, onNairaEnter}) {
  return (
    <InputGroup.Append className="d-block">
      <Button
        type="submit"
        variant="primary pr-2 rounded-xl-right"
        onClick={() => {
          if (onNairaEnter) {
            onNairaEnter(keyword);
            setKeyword("");
            return;
          }
          onEnterKey(keyword, forHtmlReport);
          setKeyword("");
        }}
      >
        <FaIcon icon="fa fa-lg fa-search" align="align-baseline"/>
      </Button>
    </InputGroup.Append>
  );
}

function NomuClipLogo() {
  const NOMUCLIP_URL = "http://intranet.nomuranow.com/NomuClip/home";
  return (
    <a
      href={NOMUCLIP_URL}
      className="py-0 pl-2 pr-0"
      title={_t("For internal use only")}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img src={nomuClipLogoSvg} className="nomuClipLogo" alt="Nomu-Clip"/>
    </a>
  );
}

const toExact = (searchText) => `"${searchText}"`;
const isNotExact = (searchText = "") => !searchText.startsWith('"');
